import { useEffect } from 'react';

interface SchemaOrgProps {
  type: 'Organization' | 'LocalBusiness' | 'WebPage' | 'Service' | 'FAQPage';
  data: Record<string, any>;
}

export function useSchemaOrg({ type, data }: SchemaOrgProps) {
  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
    
    let schema: Record<string, any> = {
      '@context': 'https://schema.org',
      '@type': type,
    };

    if (type === 'Organization' || type === 'LocalBusiness') {
      schema = {
        ...schema,
        name: data.name || 'RDM Security',
        description: data.description || 'Premium home automation solutions in South East Florida',
        url: siteUrl,
        telephone: data.telephone || '+17862829701',
        email: data.email || 'moshe@rdmsecurity.com',
        areaServed: data.areaServed || [
          {
            '@type': 'City',
            name: 'Miami',
            containedInPlace: {
              '@type': 'State',
              name: 'Florida'
            }
          },
          {
            '@type': 'City',
            name: 'Tampa',
            containedInPlace: {
              '@type': 'State',
              name: 'Florida'
            }
          }
        ],
        openingHoursSpecification: data.openingHours || [
          {
            '@type': 'OpeningHoursSpecification',
            dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday'],
            opens: '08:00',
            closes: '18:00'
          },
          {
            '@type': 'OpeningHoursSpecification',
            dayOfWeek: 'Friday',
            opens: '08:00',
            closes: '15:00'
          },
          {
            '@type': 'OpeningHoursSpecification',
            dayOfWeek: ['Saturday', 'Sunday'],
            opens: '00:00',
            closes: '23:59',
            description: 'Emergency Calls Only'
          }
        ],
        priceRange: data.priceRange || '$$$',
        ...data
      };
    } else if (type === 'WebPage') {
      schema = {
        ...schema,
        name: data.name,
        description: data.description,
        url: `${siteUrl}${data.url || ''}`,
        ...data
      };
    } else if (type === 'Service') {
      schema = {
        ...schema,
        serviceType: data.serviceType,
        provider: {
          '@type': 'Organization',
          name: 'RDM Security',
          telephone: '+17862829701',
          email: 'moshe@rdmsecurity.com'
        },
        areaServed: {
          '@type': 'State',
          name: 'Florida'
        },
        ...data
      };
    } else {
      schema = { ...schema, ...data };
    }

    const scriptId = `schema-${type.toLowerCase()}`;
    let scriptTag = document.getElementById(scriptId);
    
    if (!scriptTag) {
      scriptTag = document.createElement('script');
      scriptTag.id = scriptId;
      scriptTag.type = 'application/ld+json';
      document.head.appendChild(scriptTag);
    }
    
    scriptTag.textContent = JSON.stringify(schema);

    return () => {
      const existingScript = document.getElementById(scriptId);
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, [type, data]);
}

export function generateBreadcrumbSchema(items: Array<{ name: string; url: string }>) {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: `${siteUrl}${item.url}`
    }))
  };
}

export const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "RDM Security",
  "url": import.meta.env.VITE_SITE_URL || 'https://example.com',
  "logo": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/logo.png`,
  "description": "Premium home automation solutions specializing in Control4, Savant, RTI, and Brilliant systems. Serving South East Florida and West Coast Tampa South with fiber optic installation, structured cabling, security cameras, and smart home integration.",
  "address": {
    "@type": "PostalAddress",
    "addressRegion": "FL",
    "addressCountry": "US"
  },
  "geo": {
    "@type": "GeoCoordinates",
    "latitude": 25.7617,
    "longitude": -80.1918
  },
  "areaServed": [
    {
      "@type": "Place",
      "name": "South East Florida"
    },
    {
      "@type": "Place",
      "name": "West Coast Tampa South"
    }
  ]
};

export const localBusinessSchema = {
  "@context": "https://schema.org",
  "@type": "ElectronicsStore",
  "name": "RDM Security",
  "image": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/logo.png`,
  "url": import.meta.env.VITE_SITE_URL || 'https://example.com',
  "telephone": "+17862829701",
  "email": "moshe@rdmsecurity.com",
  "address": {
    "@type": "PostalAddress",
    "addressRegion": "FL",
    "addressCountry": "US"
  }
};
